import { useEffect, useRef } from "react";
import api from "@/lib/api";

/**
 * Automatically keeps a driver "online" (server-side heartbeat + live geo)
 * for the entire duration of an active route — no manual Go Online / Go
 * Offline toggle needed.
 *
 * Behavior:
 *  - Polls `/routes/mine/active` every 30s.
 *  - When a route in status `in_progress` or `paused` is found for THIS
 *    driver, starts a 60s heartbeat ping + a geolocation watch. Position
 *    from the watch is sent along with each heartbeat.
 *  - When the poll returns no active route (route completed / cancelled),
 *    stops the heartbeat loop and clears the geo watch → the driver
 *    naturally drops offline (server considers heartbeats >5 min stale).
 *
 * Safe to mount multiple times but designed to be mounted ONCE at the app
 * shell for users with role === "driver".
 */
export default function useDriverHeartbeat(enabled) {
  const intervalRef = useRef(null);
  const watchIdRef = useRef(null);
  const pollRef = useRef(null);
  const coordsRef = useRef(null);
  const isPingingRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;

    const ping = async () => {
      const c = coordsRef.current || {};
      try {
        await api.post("/driver/heartbeat", {
          lat: c.lat ?? 0,
          lng: c.lng ?? 0,
          accuracy: c.accuracy ?? null,
        });
      } catch {
        /* next tick will retry */
      }
    };

    const startPinging = () => {
      if (isPingingRef.current) return;
      isPingingRef.current = true;
      if (navigator.geolocation && !watchIdRef.current) {
        watchIdRef.current = navigator.geolocation.watchPosition(
          (pos) => {
            coordsRef.current = {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
            };
          },
          () => {},
          { enableHighAccuracy: true, maximumAge: 8000, timeout: 15000 },
        );
      }
      ping();
      intervalRef.current = setInterval(ping, 60_000);
    };

    const stopPinging = () => {
      if (!isPingingRef.current) return;
      isPingingRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (watchIdRef.current && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };

    const checkActiveRoute = async () => {
      try {
        const r = await api.get("/routes/mine/active");
        const active = r?.data;
        const status = active?.status;
        if (active && (status === "in_progress" || status === "paused")) {
          startPinging();
        } else {
          stopPinging();
        }
      } catch {
        // Endpoint 404 (no active route) → definitely go offline.
        stopPinging();
      }
    };

    checkActiveRoute();
    pollRef.current = setInterval(checkActiveRoute, 30_000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      stopPinging();
    };
  }, [enabled]);
}
